import { useState, useEffect, useRef, useCallback } from "react";
import {
  Copy,
  Wand2,
  ChevronDown,
  ChevronUp,
  Check,
  Loader2,
  Lightbulb,
  Cpu,
  Gauge,
  Flag,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";

/** State machine per prompt-refiner-spec */
type RefinerState = "IDLE" | "LOADING" | "DIFF" | "APPLIED";

const MIN_CHARS = 10;
const MAX_CHARS = 8000;
const DEBOUNCE_MS = 500;

const MODEL_LABELS: Record<string, string> = {
  opus: "Opus 4.6",
  sonnet: "Sonnet 4.6",
  haiku: "Haiku 4.5",
};

const EFFORT_LABELS: Record<string, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  max: "Max",
};

const PROJECT_OPTIONS = [
  { value: "", label: "Nenhum projeto" },
  { value: "~/Projetos/licitabot", label: "LicitaBot — SaaS Licitacoes" },
  { value: "~/newortho", label: "NewOrtho — Site Institucional" },
  { value: "~/Skills-dax", label: "Skills-dax — Skills Platform" },
  { value: "~/aktibone", label: "AktiBone — Biovidro Site" },
  { value: "~/Projetos/portal-fontan", label: "Portal FONTAN" },
  { value: "~/Projetos/icarus-fontan", label: "ICARUS ERP — OPME" },
  { value: "~/Projetos/fontan-ceo", label: "FONTAN CEO — Plataforma Executiva" },
  { value: "custom", label: "Outro diretorio..." },
] as const;

const LS_PROJECT_KEY = "mentor-refiner-project";

interface PromptRefinerProps {
  initialPrompt?: string | null;
  onConsumed?: () => void;
}

export function PromptRefiner({ initialPrompt, onConsumed }: PromptRefinerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const [projectSelect, setProjectSelect] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_PROJECT_KEY) ?? "";
      const isKnown = PROJECT_OPTIONS.some((o) => o.value === saved);
      return isKnown ? saved : saved ? "custom" : "";
    } catch {
      return "";
    }
  });
  const [customProject, setCustomProject] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_PROJECT_KEY) ?? "";
      const isKnown = PROJECT_OPTIONS.some((o) => o.value === saved);
      return isKnown ? "" : saved;
    } catch {
      return "";
    }
  });
  const [copied, setCopied] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const [refinerState, setRefinerState] = useState<RefinerState>("IDLE");
  const preRef = useRef<HTMLPreElement>(null);
  const lastClickRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const projectCtx = projectSelect === "custom" ? customProject : projectSelect;

  const projectQuery = trpc.skills.detectProject.useQuery(
    { path: projectCtx },
    { enabled: !!projectCtx && projectCtx !== "" }
  );

  const refineMutation = trpc.prompt.refine.useMutation({
    onMutate: () => {
      setRefinerState("LOADING");
    },
    onSuccess: () => {
      setRefinerState("DIFF");
    },
    onError: () => {
      setRefinerState("IDLE");
    },
  });

  // Consume initialPrompt from QuickPrompts
  useEffect(() => {
    if (initialPrompt) {
      setInput(initialPrompt);
      setIsOpen(true);
      onConsumed?.();
    }
  }, [initialPrompt, onConsumed]);

  // Auto-expand when result arrives
  useEffect(() => {
    if (refineMutation.data) {
      setIsOpen(true);
    }
  }, [refineMutation.data]);

  // Persist project selection
  useEffect(() => {
    try { localStorage.setItem(LS_PROJECT_KEY, projectCtx); } catch { /* SSR/test */ }
  }, [projectCtx]);

  // Detect scroll overflow for scroll hint
  useEffect(() => {
    const el = preRef.current;
    if (!el || !refineMutation.data) return;
    const needsScroll = el.scrollHeight > el.clientHeight;
    setShowScrollHint(needsScroll);
  }, [refineMutation.data]);

  // Global keyboard shortcut: Cmd+K / Ctrl+K to focus refiner
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsOpen(true);
        setTimeout(() => textareaRef.current?.focus(), 100);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const handlePreScroll = useCallback(() => {
    const el = preRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
    setShowScrollHint(!atBottom);
  }, []);

  const handleRefine = () => {
    // Debounce: ignore clicks within DEBOUNCE_MS
    const now = Date.now();
    if (now - lastClickRef.current < DEBOUNCE_MS) return;
    lastClickRef.current = now;

    if (input.trim().length < MIN_CHARS) return;

    const target = (localStorage.getItem("mentor-chat-mode") ?? "claude-code") as
      | "claude-code"
      | "cowork";

    // Create abort controller for ESC cancellation
    abortRef.current = new AbortController();

    refineMutation.mutate({
      prompt: input.trim(),
      target,
      projectContext: projectCtx.trim() || undefined,
    });
  };

  // ESC key handler: cancel in-flight mutation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      if (refinerState === "LOADING") {
        abortRef.current?.abort();
        refineMutation.reset();
        setRefinerState("IDLE");
      }
    }
    // Ctrl+Enter to submit
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      handleRefine();
    }
  };

  const handleApply = () => {
    setRefinerState("APPLIED");
    // Auto-reset to IDLE after 3s
    setTimeout(() => setRefinerState("IDLE"), 3000);
  };

  const handleDiscard = () => {
    refineMutation.reset();
    setRefinerState("IDLE");
  };

  const handleCopy = async () => {
    if (!refineMutation.data?.refined_prompt) return;
    try {
      await navigator.clipboard.writeText(refineMutation.data.refined_prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      console.warn("Clipboard copy failed");
    }
  };

  const data = refineMutation.data;
  const inputLen = input.trim().length;
  const isBelowMin = inputLen > 0 && inputLen < MIN_CHARS;
  const isAboveMax = inputLen > MAX_CHARS;
  const canSubmit = inputLen >= MIN_CHARS && inputLen <= MAX_CHARS && !refineMutation.isPending;

  return (
    <div className="border-b border-border">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between px-4 py-3 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors sm:py-2"
        aria-expanded={isOpen}
        aria-controls="prompt-refiner-content"
        aria-label="Refinar prompt com IA — reescreve o texto atual mantendo sua intencao (Ctrl+K)"
      >
        <span className="flex items-center gap-1.5">
          <Wand2 className="h-3 w-3" />
          Refinar Prompt
        </span>
        {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>

      {isOpen && (
        <div
          id="prompt-refiner-content"
          className="space-y-3 px-4 pb-4"
          role="region"
          aria-label="Painel de refinamento de prompt"
        >
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Cole seu prompt em PT-BR aqui... O Mentor vai reconstruir com flags, tools e insights."
            className="min-h-[80px] resize-none bg-background text-base sm:text-sm"
            rows={3}
            aria-label="Prompt para refinamento"
            aria-describedby="prompt-refiner-charcount"
            aria-invalid={isBelowMin || isAboveMax}
            readOnly={refinerState === "LOADING"}
          />

          {isBelowMin && (
            <p className="text-[10px] text-amber-400" role="alert">
              Escreva ao menos {MIN_CHARS} caracteres para refinar
            </p>
          )}
          {isAboveMax && (
            <p className="text-[10px] text-destructive" role="alert">
              Texto muito longo. Maximo: {MAX_CHARS} caracteres
            </p>
          )}

          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <select
                value={projectSelect}
                onChange={(e) => setProjectSelect(e.target.value)}
                className="w-full rounded-md border border-border bg-input/30 px-3 py-2 text-xs text-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/50"
                aria-label="Selecionar projeto para contexto"
              >
                {PROJECT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.value && opt.value !== "custom"
                      ? `${opt.value} (${opt.label})`
                      : opt.label}
                  </option>
                ))}
              </select>
              <span
                id="prompt-refiner-charcount"
                className={`ml-2 shrink-0 text-[10px] ${
                  isAboveMax ? "text-destructive" : isBelowMin ? "text-amber-400" : "text-muted-foreground/50"
                }`}
              >
                {inputLen}/{MAX_CHARS}
                {isBelowMin && ` (min ${MIN_CHARS})`}
              </span>
            </div>
            {projectSelect === "custom" && (
              <input
                type="text"
                value={customProject}
                onChange={(e) => setCustomProject(e.target.value)}
                placeholder="Caminho do projeto: ~/Projetos/meu-projeto"
                className="mt-1.5 w-full rounded-md border border-border bg-input/30 px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/50"
                aria-label="Caminho customizado do projeto"
              />
            )}
            <p className="text-[10px] text-muted-foreground/70">
              Vincule o caminho local para que o agente analise a estrutura real do projeto
            </p>
            {projectQuery.data && projectQuery.data.stack.length > 0 && (
              <div className="flex flex-wrap gap-1 pt-1">
                <span className="text-[10px] font-medium text-muted-foreground">
                  {projectQuery.data.name}:
                </span>
                {projectQuery.data.stack.map((tech) => (
                  <span
                    key={tech}
                    className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handleRefine}
              disabled={!canSubmit}
              aria-label="Refinar prompt (Ctrl+Enter)"
              aria-busy={refinerState === "LOADING"}
            >
              {refineMutation.isPending ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Wand2 className="mr-1.5 h-3.5 w-3.5" />
              )}
              Refinar Prompt
            </Button>
            {refinerState === "LOADING" && (
              <span className="text-[10px] text-muted-foreground">
                ESC para cancelar
              </span>
            )}
            {refinerState === "APPLIED" && (
              <span className="text-[10px] text-green-400" role="status">
                Prompt refinado com sucesso
              </span>
            )}
          </div>

          {refineMutation.error && (
            <p className="text-xs text-destructive" role="alert" aria-live="assertive">
              Erro: {refineMutation.error.message}
            </p>
          )}

          {data && (
            <div className="space-y-3">
              {/* Copy toolbar + DIFF actions */}
              <div className={`flex items-center justify-between rounded-t-lg border border-b-0 border-border bg-muted/50 px-3 py-1.5${refinerState === "DIFF" ? " sticky top-0 z-10" : ""}`}>
                <span className="text-[10px] text-muted-foreground">
                  {data.refined_prompt.length} caracteres
                </span>
                <div className="flex items-center gap-1.5">
                  {refinerState === "DIFF" && (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 gap-1 px-2 text-[11px] text-destructive hover:text-destructive"
                        onClick={handleDiscard}
                        aria-label="Descartar prompt refinado"
                      >
                        <X className="h-3 w-3" />
                        Descartar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 gap-1 px-2 text-[11px] text-green-400 hover:text-green-400"
                        onClick={handleApply}
                        aria-label="Aplicar prompt refinado"
                      >
                        <Check className="h-3 w-3" />
                        Aplicar
                      </Button>
                    </>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 gap-1.5 px-2 text-[11px]"
                    onClick={handleCopy}
                    aria-label="Copiar prompt refinado para a area de transferencia"
                  >
                    {copied ? (
                      <>
                        <Check className="h-3 w-3 text-green-500" />
                        Copiado
                      </>
                    ) : (
                      <>
                        <Copy className="h-3 w-3" />
                        Copiar
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Refined prompt with scroll indicator */}
              <div className="relative -mt-3">
                <pre
                  ref={preRef}
                  className="max-h-[min(400px,40vh)] overflow-auto rounded-b-lg border border-border bg-muted/30 p-3 text-xs leading-relaxed whitespace-pre-wrap break-words font-mono"
                  onScroll={handlePreScroll}
                >
                  {data.refined_prompt}
                </pre>
                {showScrollHint && (
                  <div className="pointer-events-none absolute bottom-0 left-0 right-0 rounded-b-lg bg-gradient-to-t from-muted/80 to-transparent py-2 text-center text-[10px] text-muted-foreground">
                    Scroll para ver mais
                  </div>
                )}
              </div>

              {/* Model + Effort + Tokens */}
              <div className="flex flex-wrap items-center gap-3 text-xs">
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/10 px-2 py-0.5 font-medium text-blue-400">
                  <Cpu className="h-3 w-3" />
                  {MODEL_LABELS[data.model_recommendation] ?? data.model_recommendation}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 font-medium text-amber-400">
                  <Gauge className="h-3 w-3" />
                  Effort: {EFFORT_LABELS[data.effort_recommendation] ?? data.effort_recommendation}
                </span>
                <span className="text-muted-foreground">
                  ~{data.token_estimate} tokens
                </span>
              </div>

              {/* Collapsible details: flags, skills, insights, techniques */}
              <details className="group">
                <summary className="flex cursor-pointer items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground">
                  <span>Detalhes da reconstrucao</span>
                  <span className="text-[10px] text-muted-foreground/60">
                    {data.flags_inserted.length} flags · {data.suggested_skills.length} skills · {data.techniques_applied.length} tecnicas
                  </span>
                </summary>
                <div className="mt-2 space-y-3">
                  {/* Flags inseridas */}
                  {data.flags_inserted.length > 0 && (
                    <div>
                      <p className="mb-1.5 flex items-center gap-1 text-xs font-medium text-muted-foreground">
                        <Flag className="h-3 w-3" />
                        Flags inseridas
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {data.flags_inserted.map((flag) => (
                          <span
                            key={flag}
                            className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-400"
                          >
                            {flag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Skills sugeridas */}
                  {data.suggested_skills.length > 0 && (
                    <div>
                      <p className="mb-1.5 text-xs font-medium text-muted-foreground">
                        Skills sugeridas
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {data.suggested_skills.map((skill) => (
                          <span
                            key={skill}
                            className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Insights */}
                  {data.insights.length > 0 && (
                    <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
                      <p className="mb-1.5 flex items-center gap-1 text-xs font-medium text-amber-400">
                        <Lightbulb className="h-3 w-3" />
                        Insights
                      </p>
                      <ul className="space-y-1">
                        {data.insights.map((insight, i) => (
                          <li key={i} className="text-xs text-muted-foreground">
                            {insight}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Techniques */}
                  <div className="text-xs text-muted-foreground">
                    Tecnicas: {data.techniques_applied.join(", ")}
                  </div>
                </div>
              </details>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
